import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { 
  DndContext, 
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  GripVertical,
  Package,
  Search,
  Filter,
  X,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sortable Product Card
const SortableProductCard = ({ product, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        group relative bg-white border rounded-lg p-3 mb-2
        hover:shadow-md transition-all cursor-default
        ${isDragging ? 'shadow-xl border-red-500 z-50' : 'border-gray-200'}
      `}
    >
      {/* Drag Handle */}
      <div 
        {...attributes} 
        {...listeners}
        className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center cursor-grab active:cursor-grabbing hover:bg-gray-100 rounded-l-lg"
      >
        <GripVertical size={16} className="text-gray-400" />
      </div>

      {/* Product Info */}
      <div className="flex items-center gap-3 ml-6">
        {/* Order Number */}
        <div className="flex-shrink-0 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
          {index + 1}
        </div>

        {/* Image */}
        {product.image && (
          <img 
            src={product.image} 
            alt={product.name}
            className="w-16 h-16 object-cover rounded border"
          />
        )}

        {/* Details */}
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 truncate">{product.name}</h4>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-sm text-gray-600">{product.price} TL</span>
            <span className={`text-xs px-2 py-1 rounded ${
              product.stock > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              Stok: {product.stock}
            </span>
          </div>
        </div>

        {/* Product ID */}
        <div className="text-xs text-gray-400">
          ID: {typeof product.id === 'string' ? product.id.substring(0, 8) : product.id}
        </div>
      </div>
    </div>
  );
};

// Category Sidebar Item
const CategorySidebarItem = ({ category, isActive, onClick, productCount }) => {
  return (
    <button
      onClick={onClick}
      className={`
        w-full text-left px-4 py-3 rounded-lg mb-1 transition-all
        flex items-center justify-between group
        ${isActive 
          ? 'bg-red-600 text-white shadow-md' 
          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
        }
      `}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <Package size={18} className={isActive ? 'text-white' : 'text-red-600'} />
        <span className="font-medium truncate">{category.name}</span>
      </div>
      <div className={`
        text-xs px-2 py-1 rounded-full
        ${isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}
      `}>
        {productCount}
      </div>
    </button>
  );
};

// Main Component
const AdminProductSorting = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [stockFilter, setStockFilter] = useState('all'); // all, in-stock, out-of-stock
  const [sortFilter, setSortFilter] = useState('default'); // default, price-asc, price-desc, newest

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!selectedCategory) return;
    applyFilters();
  }, [selectedCategory, allProducts, searchQuery, stockFilter, sortFilter]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      // Fetch categories (including subcategories for filtering)
      const catRes = await fetch(`${API_URL}/api/categories/all/flat`);
      const categoriesData = await catRes.json();
      const mainCategories = categoriesData.filter(c => !c.parent_id && c.enabled);
      
      // Fetch products
      const prodRes = await fetch(`${API_URL}/api/products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const productsData = await prodRes.json();

      // Store all categories (for subcategory lookup)
      window.allCategoriesData = categoriesData;
      
      setCategories(mainCategories.sort((a, b) => (a.order || 0) - (b.order || 0)));
      setAllProducts(productsData);
      
      if (mainCategories.length > 0) {
        setSelectedCategory(mainCategories[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (!selectedCategory) {
      setDisplayedProducts([]);
      return;
    }

    // Ana kategori veya alt kategorilerinden birinde olan ürünleri getir
    const allCats = window.allCategoriesData || [];
    const subCategories = allCats.filter(c => c.parent_id === selectedCategory.id);
    
    let filtered = allProducts.filter(p => {
      if (!p.category || !Array.isArray(p.category)) return false;
      
      // Ana kategoride direkt var mı kontrol et
      if (p.category.includes(selectedCategory.slug)) return true;
      
      // Alt kategorilerinde var mı kontrol et
      return subCategories.some(sub => p.category.includes(sub.slug));
    });

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Stock filter
    if (stockFilter === 'in-stock') {
      filtered = filtered.filter(p => p.stock > 0);
    } else if (stockFilter === 'out-of-stock') {
      filtered = filtered.filter(p => p.stock === 0);
    }

    // Sort filter
    if (sortFilter === 'price-asc') {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortFilter === 'price-desc') {
      filtered.sort((a, b) => b.price - a.price);
    } else if (sortFilter === 'newest') {
      filtered.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    } else {
      // Default: sort by display_order
      filtered.sort((a, b) => (a.display_order || 999999) - (b.display_order || 999999));
    }

    setDisplayedProducts(filtered);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = displayedProducts.findIndex(p => p.id === active.id);
    const newIndex = displayedProducts.findIndex(p => p.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Reorder locally
    const reordered = arrayMove(displayedProducts, oldIndex, newIndex);
    
    setDisplayedProducts(reordered);
    setHasUnsavedChanges(true);
  };

  const handleSaveOrder = async () => {
    if (!selectedCategory) return;
    
    try {
      setSaving(true);
      const token = localStorage.getItem('adminToken');
      const categorySlug = selectedCategory.slug;

      // Send updates to backend ONE BY ONE
      let successCount = 0;
      const failedProducts = [];
      
      for (let i = 0; i < displayedProducts.length; i++) {
        const product = displayedProducts[i];
        const newOrder = i + 1;
        
        try {
          const response = await fetch(`${API_URL}/api/products/${product.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              display_order: newOrder
            })
          });

          if (response.ok) {
            successCount++;
          } else {
            const errorText = await response.text();
            failedProducts.push(product.name);
          }
        } catch (err) {
          failedProducts.push(product.name);
        }
      }

      // Refetch from backend
      await fetchData();
      
      // Wait a bit for state to update
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Re-select the same category to trigger filter
      const updatedCategory = categories.find(c => c.slug === categorySlug);
      if (updatedCategory) {
        setSelectedCategory(updatedCategory);
      }
      
      setHasUnsavedChanges(false);
      
      // Show success message
      const successDiv = document.createElement('div');
      successDiv.className = 'fixed top-20 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successDiv.innerHTML = `
        <div class="flex items-center gap-3">
          <span class="text-2xl">✅</span>
          <div>
            <div class="font-bold text-lg">${successCount} ürün kaydedildi!</div>
            <div class="text-sm opacity-90">Sıralama güncellendi</div>
            ${failedProducts.length > 0 ? `<div class="text-xs opacity-75 mt-1">⚠️ ${failedProducts.length} ürün başarısız</div>` : ''}
          </div>
        </div>
      `;
      document.body.appendChild(successDiv);
      setTimeout(() => successDiv.remove(), 4000);
      
    } catch (error) {
      alert(`❌ Sıralama kaydedilemedi!\n\nHata: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const getProductCountForCategory = (categorySlug) => {
    return allProducts.filter(p => 
      p.category && Array.isArray(p.category) && p.category.includes(categorySlug)
    ).length;
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStockFilter('all');
    setSortFilter('default');
  };

  const hasActiveFilters = searchQuery || stockFilter !== 'all' || sortFilter !== 'default';

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Yükleniyor...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="flex gap-6 h-[calc(100vh-120px)]">
        {/* Left Sidebar - Categories */}
        <div className="w-72 flex-shrink-0">
          <div className="bg-gray-50 rounded-lg p-4 h-full overflow-y-auto">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Kategoriler</h2>
            <div className="space-y-1">
              {categories.map(category => (
                <CategorySidebarItem
                  key={category.id}
                  category={category}
                  isActive={selectedCategory?.id === category.id}
                  onClick={() => setSelectedCategory(category)}
                  productCount={getProductCountForCategory(category.slug)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel - Products */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {selectedCategory ? selectedCategory.name : 'Ürün Sıralaması'}
                </h1>
                <p className="text-gray-600 mt-1">
                  {displayedProducts.length} ürün • Sürükle-bırak ile sıralayın
                </p>
              </div>
              
              {/* Save Button */}
              <button
                onClick={handleSaveOrder}
                disabled={!hasUnsavedChanges || saving}
                className={`
                  px-6 py-3 rounded-lg font-medium transition-all
                  ${hasUnsavedChanges && !saving
                    ? 'bg-red-600 text-white hover:bg-red-700 shadow-md hover:shadow-lg' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }
                `}
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Kaydediliyor...
                  </span>
                ) : (
                  <>
                    💾 Sıralamayı Kaydet
                    {hasUnsavedChanges && <span className="ml-2 text-xs">(Değişiklikler var)</span>}
                  </>
                )}
              </button>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Search */}
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Ürün ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              {/* Stock Filter */}
              <select
                value={stockFilter}
                onChange={(e) => setStockFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="all">Tüm Stok Durumları</option>
                <option value="in-stock">Stokta Var</option>
                <option value="out-of-stock">Stokta Yok</option>
              </select>

              {/* Sort Filter */}
              <select
                value={sortFilter}
                onChange={(e) => setSortFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="default">Varsayılan Sıralama</option>
                <option value="price-asc">Artan Fiyat</option>
                <option value="price-desc">Azalan Fiyat</option>
                <option value="newest">Son Eklenenler</option>
              </select>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-3 flex items-center gap-2 text-sm text-red-600 hover:text-red-700"
              >
                <X size={16} />
                Filtreleri Temizle
              </button>
            )}
          </div>

          {/* Products List */}
          <div className="flex-1 bg-white rounded-lg shadow-sm p-6 overflow-y-auto">
            {displayedProducts.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {searchQuery || hasActiveFilters 
                  ? '🔍 Filtre kriterlerine uygun ürün bulunamadı' 
                  : '📦 Bu kategoride ürün yok'}
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={displayedProducts.map(p => p.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {displayedProducts.map((product, index) => (
                    <SortableProductCard
                      key={product.id}
                      product={product}
                      index={index}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProductSorting;
